import React, { FC, useState, useRef } from "react";
import ModalTitleContainer from "../../../../components/Modal/components/ModalTitleContainer";
import { MenuItem } from "@mui/material";
import styles from "./components.module.scss";
import Api from "../../../../api";
import { exitReact } from "../../../../utils";
import dayjs from "dayjs";
import { JobPropsWithHygienist, UserAccountProps } from "utils/types";
import { FormMaterialSelector } from "components/Selector";
import { FormMaterialInput } from "components/Inputs/MaterialInput";
import Popup from "../../../../components/Popup";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import joi from "joi";
import FixedLoader from "../../../../components/FixedLoader/FixedLoader";
import { CancelOffer, CancelOfferBox, LinkButton } from "../../../hygienist/ConfirmedOfferPage/ConfirmedOffer.styles";
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import { usePersistedState } from "../../../../hooks";
import { entities } from "../../../../reduxStore";
import { FormControlLabel, Checkbox } from "@mui/material";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { buildOfficeCancelFirstWriteBody } from "./officeCancelFirstWrite";
import { getOfficeCancellationErrorMessage } from "utils/officeCancellationError";
import { openExternalUrlFromLink } from "utils/nativeApp";

const CANCELLATION_POLICY_URL = 'https://support.tempstars.com/hc/en-us/articles/360047269733-Cancelling-Shifts-Dental-Offices';

export const schema = joi
    .object({
        reason: joi.string().label('Reason').required(),
        details: joi.string().label('Details').required(),
        repostShift: joi.boolean().optional(),
    })
    .required()

const CancelThanksPopup = ({
    firstName,
    onClose,
    isReposted,
    currentReason
}: {
    firstName: string,
    onClose: () => void,
    isReposted: boolean,
    currentReason: string
}) => {
    const defaultMessage = currentReason === 'onBehalfOfTalent' 
        ? "Thanks for letting us know about this cancellation, we'll be in touch if we need any more information."
        : `We do understand patient schedule and needs can change, and we\'ll do our best to help ${firstName} find a replacement shift.`;
    return (
        <div className={styles.popupContainer}>
            <ModalTitleContainer title={'Got it, Thanks!'} />
            <div className={styles.modalDescription}>
                <p>{defaultMessage}</p>
                {isReposted && <p>Your shift just got reposted!</p>}
            </div>

            <StyledButton
                variant="contained"
                color="primary"
                size="large"
                id="continue-button"
                onClick={onClose}
                className="no-fastclick"
            >
                Submit
            </StyledButton>
        </div>
    )
}

interface CancelShiftModalProps {
    job: JobPropsWithHygienist,
    onSubmit: () => void,
}

export const CancelShiftModal: FC<CancelShiftModalProps> = ({ job, onSubmit }) => {
    const [showThanksPopup, setShowThanksPopup] = useState<boolean>(false);
    const [isOpenCancelShiftModal, setIsOpenCancelShiftModal] = useState<boolean>(false);
    const history = useHistory();
    const repostChoiceRef = useRef<'post' | 'edit' | null>(null);
    const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});

    const [repostChoice, setRepostChoice] = useState<'post' | 'edit' | null>(null);
    const [isReposted, setIsReposted] = useState<boolean>(false);
    const [currentReason, setCurrentReason] = useState<string>('');

    const {
        control,
        handleSubmit,
        watch,
        register,
        formState: { errors }
    } = useForm({
        defaultValues: {
            reason: '',
            details: '',
            repostShift: false,
        },
        resolver: joiResolver(schema),
    });

    const currentReasonValue = watch('reason');
    const repostShift = watch('repostShift');

    let user = usePersistedState<UserAccountProps>({
        entityName: entities.user
    })[0];

    const start = dayjs(job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart);
    const less24h = start.diff(dayjs(), 'hour') < 24;

    const description = `${job.hygienist.firstName} has given up other opportunities in order to commit to working at your office.\n\n` +
        `We closely monitor booking and cancellation track records. According to our records, your office has previously cancelled ${user?.dentist?.cancelledShiftsNum} shifts.\n\n` +
        (less24h ? `It's less than 24hrs before the shift starts.\n\nPlease review TempStars Cancellation Policy that your office agreed to when booking this shift.\n\n` : '') +
        'We understand things happen, but repeat cancellations may impact your ability to use TempStars. Are you sure?';

    const [loading, setLoading] = useState(false);

    const validateForm = (formData: any) => {
        const newErrors: {[key: string]: string} = {};
        
        if (!formData.reason) {
            newErrors.reason = 'Please choose a reason';
        }
        
        if (!formData.details) {
            newErrors.details = 'Please provide cancellation details';
        }
        
        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const onCancel = async (formData: any) => {
        if (!validateForm(formData)) {
            return;
        }
        
        setLoading(true);
        try {
            // T2-6714: persist reason/details (+ on-behalf repost) on the initial cancel write
            const firstWrite = buildOfficeCancelFirstWriteBody(formData, {
                reasonValue: currentReasonValue,
                repostShift: !!repostShift,
            });
            if (firstWrite.shouldRepost) {
                formData.repostShift = true;
                repostChoiceRef.current = 'post';
            }

            const { dentistCancelledId } = await Api.dentistCancelJob(job.id, {
                reason: firstWrite.reason,
                details: firstWrite.details,
                ...(firstWrite.repostShift ? { repostShift: true } : {}),
            });

            // Second PUT is email/compat only — cancellation already succeeded on DELETE
            if (dentistCancelledId != null) {
                try {
                    await Api.updateDentistCancelled({
                        ...formData,
                        dentistCancelledId,
                    });
                } catch (followUpErr) {
                    console.warn('[CancelShift] updateDentistCancelled follow-up failed', followUpErr);
                }
            }

            setLoading(false);
            handleReasonSubmitComplete({
                repostChoice: repostChoiceRef.current,
                repostShift: formData.repostShift,
                reason: formData.reason
            });
        } catch (e) {
            setLoading(false);
            setValidationErrors({ form: getOfficeCancellationErrorMessage(e) });
        }
    };

    const cancelShift = () => {
        setIsOpenCancelShiftModal(true);
    }

    const onClose = () => setIsOpenCancelShiftModal(false);

    const cancelReasons = [
        {
            value: 'permanentStaffCovered',
            text: 'Permanent Staff Covered',
        },
        {
            value: 'patientsCancelled',
            text: 'Patients Cancelled',
        },
        {
            value: 'doubleBooked',
            text: 'Double-booked with another temp',
        },
        {
            value: 'badExperiende',
            text: 'Previous bad experience',
        },
        {
            value: 'tempNoShowed',
            text: 'Temp No-Showed',
        },
        {
            value: 'other',
            text: 'Other',
        },
        {
            value: 'onBehalfOfTalent',
            text: `The ${job.hygienist.type ? 'Dental Assistant' : 'Dental Hygienist'} asked me to cancel on their behalf`,
        },
    ];

    const handleReasonSubmitComplete = ({ repostChoice: choice, repostShift, reason }: { repostChoice: 'post' | 'edit' | null, repostShift: boolean, reason: string }) => {
        setRepostChoice(choice);
        setIsReposted(repostShift);
        setCurrentReason(reason);
        setShowThanksPopup(true);
        setIsOpenCancelShiftModal(false);
    }

    const handleThanksClose = () => {
        setShowThanksPopup(false);
        if (repostChoice && repostChoice === 'edit') {
            history.push('postJob', {
                mode: 'edit',
                job,
                date: dayjs(job.startDate).toISOString(),
                shift: job.shifts[0],
                role: job.hygienist.type ? '1' : '0',
                location: job.location,
                from: job.shifts[0].postedStart,
                to: job.shifts[0].postedEnd,
            });
        } else {
            history.push({ pathname: 'dentistHome' });
        }
    }

    return (
        <>
            <Popup opened={isOpenCancelShiftModal} onClose={onClose}>
                <div className={styles.popupContainer}>
                    <ModalTitleContainer title="Are you sure you need to cancel this shift?" onClose={onClose} center={false} />
                    <div className={styles.modalDescription}>
                        <p>{description}</p>
                    </div>

                    <form className={styles.styledReasonForm}>
                        <FormMaterialSelector
                            control={control}
                            className={styles.styledSelector}
                            name="reason"
                            label="Please choose a reason"
                        >
                            {cancelReasons.map((reason: { value: string; text: string }) => (
                                <MenuItem className={styles.rateMenuItem} value={reason.value} key={reason.value}>
                                    {reason.text}
                                </MenuItem>
                            ))}
                        </FormMaterialSelector>

                        <FormMaterialInput
                            control={control}
                            className={styles.styledInput}
                            label="Cancellation Details"
                            name="details"
                            type="text"
                            multiline
                            rows={3}
                        />

                        {(validationErrors.reason || validationErrors.details || validationErrors.form) && (
                            <p style={{ color: '#FB7672', marginTop: 8 }}>
                                {validationErrors.form
                                    || validationErrors.reason
                                    || validationErrors.details}
                            </p>
                        )}

                        {currentReasonValue === 'onBehalfOfTalent' && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...register('repostShift')}
                                    />
                                }
                                label="I need the shift re-posted"
                            />
                        )}

                        <div className={styles.shiftDetails}>
                            <div>Date: <b>{dayjs(job.shifts[0].shiftDate).format('dddd, MMM D')}</b></div>
                            <div>Hours: <b>{job.shifts[0].postedStart} - {job.shifts[0].postedEnd}</b></div>
                            <div>Rate: <b>${job.proposedRate}/hr</b></div>
                        </div>

                        <div className={styles.buttonContainer}>
                            <StyledButton
                                variant="outlined"
                                color="primary"
                                size="large"
                                onClick={onClose}
                                className={clsx(styles.flexButton, 'no-fastclick')}
                            >
                                Keep my Shift
                            </StyledButton>
                            <StyledButton
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit(onCancel)}
                                className={clsx(styles.flexButton, 'no-fastclick')}
                            >
                                Cancel Shift
                            </StyledButton>
                        </div>
                    </form>
                </div>
            </Popup>

            <Popup opened={showThanksPopup}>
                <CancelThanksPopup
                    firstName={job.hygienist.firstName}
                    isReposted={isReposted}
                    onClose={handleThanksClose}
                    currentReason={currentReason}
                />
            </Popup>

            <CancelOfferBox>
                <CancelOffer
                    variant="outlined"
                    color="secondary"
                    size="large"
                    id="continue-button"
                    onClick={cancelShift}
                    className="no-fastclick"
                >
                    Cancel Shift
                </CancelOffer>
                <LinkButton
                    href={CANCELLATION_POLICY_URL}
                    target="_system"
                    rel="noopener noreferrer"
                    className="external no-fastclick"
                    id="dentist-confirmed-cancellation-policy-link"
                    data-testid="dentist-confirmed-cancellation-policy-link"
                    aria-label="Open cancellation and membership review policy"
                    content-desc="dentist-confirmed-cancellation-policy-link"
                    onClick={(event) => openExternalUrlFromLink(event.nativeEvent, CANCELLATION_POLICY_URL)}
                >
                    TempStars Cancellation and Membership Review Policy
                </LinkButton>
            </CancelOfferBox>

            <FixedLoader loading={loading} />
        </>
    );
}
